import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";
import { LayoutDashboard, Package, FolderHeart, ShoppingCart, Gift, Percent, Mail, MessageCircle, FileEdit, Settings, LogOut, Images, Star, ArrowLeft, Menu, KeyRound, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../lib/api";
import Dashboard from "./Dashboard";
import Products from "./Products";
import { Categories, Slides } from "./Catalog";
import Orders from "./Orders";
import { Vouchers, Discounts } from "./Promo";
import { Newsletter, Reviews, Customers } from "./People";
import Messenger from "./Messenger";
import Content from "./Content";
import SettingsPage from "./Settings";
import ApiSettings from "./ApiSettings";
import Emails from "./Emails";
import Campaigns from "./Campaigns";

const MENU = [
  ["", "Přehled", LayoutDashboard, Dashboard], ["objednavky", "Objednávky", ShoppingCart, Orders], ["produkty", "Produkty", Package, Products],
  ["kategorie", "Kategorie", FolderHeart, Categories], ["hero", "Hero slidy", Images, Slides], ["poukazy", "Dárkové poukazy", Gift, Vouchers],
  ["slevy", "Slevové kódy", Percent, Discounts], ["newsletter", "Odběratelé", Mail, Newsletter], ["kampane", "Newsletter kampaně", Send, Campaigns], ["emaily", "Automatické e-maily", MailCheck, Emails], ["hodnoceni", "Hodnocení", Star, Reviews],
  ["zakaznici", "Zákazníci & zprávy", Mail, Customers], ["messenger", "Messenger robot", MessageCircle, Messenger], ["obsah", "Obsah webu", FileEdit, Content], ["nastaveni", "Nastavení & faktury", Settings, SettingsPage], ["api", "API & nastavení", KeyRound, ApiSettings],
];

function PinGate({ onOk }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (p) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/login", { pin: p });
      sessionStorage.setItem("mps_admin", data.token);
      onOk();
    } catch (e) {
      toast.error(errMsg(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  const press = (d) => {
    const n = (pin + d).slice(0, 6);
    setPin(n);
    if (n.length === 6) submit(n);
  };
  return (
    <div className="min-h-screen paper flex items-center justify-center p-6" data-testid="admin-pin-gate">
      <div className="bg-white sketch border-2 border-dashed border-[#f0cfe0] p-8 w-full max-w-sm text-center">
        <img src="/logo.png" alt="" className="h-20 mx-auto" />
        <h1 className="text-2xl font-semibold mt-3">Administrace</h1>
        <p className="text-sm text-[var(--muted)]">Zadejte PIN kód</p>
        <div className="flex justify-center gap-2 my-6">{[...Array(6)].map((_, i) => <span key={i} className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? "bg-[var(--pink)]" : "bg-[var(--pink-soft)]"}`} />)}</div>
        <input data-testid="admin-pin-input" type="password" inputMode="numeric" autoFocus value={pin} disabled={busy} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setPin(v); if (v.length === 6) submit(v); }} className="field text-center tracking-[0.6em] text-xl mb-4" />
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, "←", 0, "OK"].map((k) => (
            <button key={k} data-testid={`admin-pin-key-${k}`} onClick={() => (k === "←" ? setPin(pin.slice(0, -1)) : k === "OK" ? submit(pin) : press(String(k)))} className="h-14 rounded-2xl bg-[var(--blush)] font-display text-xl hover:bg-[var(--pink-soft)] active:scale-95 transition-transform">{k}</button>
          ))}
        </div>
        <Link to="/" className="inline-flex items-center gap-1 text-sm mt-6 text-[var(--muted)]"><ArrowLeft className="w-4 h-4" /> Zpět na web</Link>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const [ok, setOk] = useState(!!sessionStorage.getItem("mps_admin"));
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const out = () => setOk(false);
    window.addEventListener("mps-admin-logout", out);
    return () => window.removeEventListener("mps-admin-logout", out);
  }, []);
  if (!ok) return <PinGate onOk={() => setOk(true)} />;
  const logout = () => {
    sessionStorage.removeItem("mps_admin");
    setOk(false);
  };
  return (
    <div className="min-h-screen bg-[#fbf8fa] lg:flex" data-testid="admin-app">
      <button className="lg:hidden fixed top-3 right-3 z-50 w-11 h-11 rounded-full bg-white shadow grid place-items-center" onClick={() => setOpen(!open)} data-testid="admin-menu-toggle"><Menu className="w-5 h-5" /></button>
      <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 bg-white border-r border-[#f1e4ea] overflow-y-auto`}>
        <Link to="/" className="flex items-center gap-2 p-5"><img src="/logo.png" alt="" className="h-12" /><span className="font-display leading-tight">CRM<br /><span className="text-[var(--pink)] text-sm">Materiály pro školky</span></span></Link>
        <nav className="px-3 pb-6 space-y-1">
          {MENU.map(([p, l, I]) => (
            <NavLink key={p} to={`/admin/${p}`} end onClick={() => setOpen(false)} data-testid={`admin-nav-${p || "prehled"}`} className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold transition-colors ${isActive ? "bg-[var(--pink-soft)] text-[var(--pink-dark)]" : "hover:bg-[var(--blush)]"}`}><I className="w-4 h-4" />{l}</NavLink>
          ))}
          <button onClick={logout} data-testid="admin-logout-btn" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold hover:bg-[var(--blush)]"><LogOut className="w-4 h-4" /> Odhlásit</button>
        </nav>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-8">
        <Routes>
          {MENU.map(([p, , , C]) => <Route key={p} path={p} element={<C />} />)}
          <Route index element={<Dashboard />} />
        </Routes>
      </main>
    </div>
  );
}
